import React from 'react';
import { Row, Col, Table, Card } from 'reactstrap';
import CodeBlock from '../CodeBlock';
import { HashLink as Link } from 'react-router-hash-link';

function SearchResultSection() {
    return (
        <section id="searchresult">
            <Row>
                <Col>
                    <h3>Search Results</h3>
                    <p>
                        <code>/Search</code> response model
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Candidates</td>
                                <td>object array</td>
                                <td>
                                    Returns an array of <Link to="#searchresultcandidate">Candidate</Link> objects.
                                </td>
                            </tr>
                            <tr>
                                <td>TotalResultsCount</td>
                                <td>integer</td>
                                <td>Total number of candidates returned.</td>
                            </tr>
                            <tr>
                                <td>Facets</td>
                                <td>object array</td>
                                <td>
                                    Returns an array of <Link to="#facets">facet</Link> objects.
                                </td>
                            </tr>
                            <tr>
                                <td>SearchId</td>
                                <td>string</td>
                                <td>Unique identifier for search request.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"Candidates": […],\n\t"TotalResultsCount": 55964,\n\t"Facets": […],\n\t"SearchId": "bELYJxD3zJo/lzpXOAjFC2h/2nsky/6oX4LgmYMcCOIoTe96+ni/2PBXo231rg0p"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="searchresultcandidate">Candidate Object</h4>
                    <p>
                        The candidate model returned in <Link to="#searchresult">search results</Link>.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Id</td>
                                <td>integer</td>
                                <td>Our internal identifier for given candidate.</td>
                            </tr>
                            <tr>
                                <td>Relevancy</td>
                                <td>integer</td>
                                <td>Relevancy score ranging from 0-100, higher is better.</td>
                            </tr>
                            <tr>
                                <td>ForeName</td>
                                <td>string</td>
                                <td>Candidates first name.</td>
                            </tr>
                            <tr>
                                <td>Surname</td>
                                <td>string</td>
                                <td>Candidates last name.</td>
                            </tr>
                            <tr>
                                <td>DesiredJobTitle</td>
                                <td>string</td>
                                <td>Candidates requested job title.</td>
                            </tr>
                            <tr>
                                <td>CurrentJobTitle</td>
                                <td>string</td>
                                <td>Candidates current job title.</td>
                            </tr>
                            <tr>
                                <td>WorkExperience</td>
                                <td>string</td>
                                <td>Duration of time spent in current job title.</td>
                            </tr>
                            <tr>
                                <td>Title</td>
                                <td>string</td>
                                <td>Candidates title.</td>
                            </tr>
                            <tr>
                                <td>PersonalSummary</td>
                                <td>string</td>
                                <td>Candidates personal summary.</td>
                            </tr>
                            <tr>
                                <td>CvSnippet</td>
                                <td>string</td>
                                <td>
                                    Extract taken from CV. Not provided when performing a blank search{' '}
                                    <i>(no keywords in SearchText or TargetedSearch properties).</i>
                                </td>
                            </tr>
                            <tr>
                                <td>KeySkills</td>
                                <td>string</td>
                                <td>List of skills extracted from candidates&apos; profile and CV.</td>
                            </tr>
                            <tr>
                                <td>IsAnonymous</td>
                                <td>boolean</td>
                                <td>
                                    This flag informs that candidate profile is anonymised. Information like FirstName, LastName,
                                    Phone and Email are not being returned. <code>CV preview</code> and <code>Download CV</code>{' '}
                                    options are not available for such candidates.
                                </td>
                            </tr>
                            <tr>
                                <td>WebsiteLinks</td>
                                <td>object array</td>
                                <td>
                                    Returns an array of <Link to="#websitelink">WebsiteLink</Link> objects.
                                </td>
                            </tr>
                            <tr>
                                <td>CurrentLocation</td>
                                <td>string</td>
                                <td>Candidates&apos; current location.</td>
                            </tr>
                            <tr>
                                <td>DesiredLocations</td>
                                <td>string array</td>
                                <td>Candidates&apos; selected list of desired job locations.</td>
                            </tr>
                            <tr>
                                <td>Distance</td>
                                <td>float</td>
                                <td>
                                    Distance from postcode or current location in search criteria, if location not defined distance
                                    is always equal to 0
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#candidatesalaryresponse">CandidateSalary</Link>
                                </td>
                                <td>object</td>
                                <td>
                                    Returns a <Link to="#candidatesalaryresponse">CandidateSalary</Link> object.
                                </td>
                            </tr>
                            <tr>
                                <td>DesiredSalary</td>
                                <td>object</td>
                                <td>
                                    Returns a <Link to="#candidatesalaryresponse">CandidateSalary</Link> object.
                                </td>
                            </tr>
                            <tr className="table-danger">
                                <td>LastUpdatedDate (Obsolete)</td>
                                <td>date</td>
                                <td>
                                    This has been replaced by <Link to="#lastactivedate">LastActiveDate</Link> and will always display
                                    the value of <Link to="#lastactivedate">LastActiveDate</Link>.
                                </td>
                            </tr>
                            <tr className="table-danger">
                                <td>LastApplicationDate (Obsolete)</td>
                                <td>date</td>
                                <td>
                                    This has been replaced by <Link to="#lastactivedate">LastActiveDate</Link> and will always display
                                    the value of <Link to="#lastactivedate">LastActiveDate</Link>. dd/MM/yyyy HH:mm:ss If the
                                    candidate has requested their application data be hidden then the endpoint will return no data.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#applicationsdata">ApplicationsData</Link>
                                </td>
                                <td>object</td>
                                <td>
                                    If the candidate has requested their application data be hidden then the endpoint will return no
                                    data.
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#viewinfo">ViewInfo</Link>
                                </td>
                                <td>object</td>
                                <td>Object containing recruiter viewing data on this candidate.</td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#applicationssummary">ApplicationsSummary</Link>
                                </td>
                                <td>object</td>
                                <td>
                                    Summary of candidates applications grouped by normalised job title. If the candidate has
                                    requested their application data be hidden then the endpoint will return no data.
                                </td>
                            </tr>
                            <tr>
                                <td id="lastactivedate">LastActiveDate</td>
                                <td>date</td>
                                <td>
                                    dd/MM/yyyy HH:mm:ss The date of their last application or profile update date, which ever is the
                                    most recent.
                                </td>
                            </tr>
                            <tr>
                                <td>JobType</td>
                                <td>string array</td>
                                <td>
                                    Candidates requested job type as defined by the values outlined by the dictionary endpoint{' '}
                                    <code>/Dictionary/JobTypes</code>.
                                </td>
                            </tr>
                            <tr>
                                <td>RegisteredDate</td>
                                <td>date</td>
                                <td>dd/MM/yyyy HH:mm:ss When the candidate registered their profile.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"Id": 11111111,\n\t"ForeName": "John",\n\t"Surname": "Smith",\n\t"DesiredJobTitle": "warehouse operative",\n\t"CurrentJobTitle": "IT Technician Support",\n\t"WorkExperience": "11-20 years",\n\t"Title": "Mr",\n\t"PersonalSummary": "A highly motivated and hardworking individual, who has recently completed their A-Levels, achieving excellent grades in both Maths and Science. Seeking an apprenticeship in the engineering industry to build upon a keen scientific interest and start a career as a maintenance engineer.",\n\t"KeySkills": "IT,infrastructure,training,sales,providing training,organised,Responsible,Technology,consulting,implementation,hardware,network,folders,configuration,TCP / IP,networks,IT Management",\n\t"IsAnonymous": false,\n\t"WebsiteLinks": [\n\t\t{\n\t\t\t"Url": "https://www.linkedin.com/in/john-smith-12345",\n\t\t\t"Type": "linkedin"\n\t\t}\n\t],\n\t"CurrentLocation": "North West London NW3 7BA",\n\t"DesiredLocations": [\n\t\t"London"\n\t],\n\t"Relevancy": 69,\n\t"Distance": 4.7,\n\t"CurrentSalary": {\n\t\t"LowerValue": 9,\n\t\t"UpperValue": 9.99,\n\t\t"RateType": "Hour"\n\t},\n\t"DesiredSalary": {\n\t\t"LowerValue": 20000,\n\t\t"UpperValue": 21999,\n\t\t"RateType": "Year"\n\t},\n\t"LastUpdatedDate": "06/04/2021 11:00:00",\n\t"LastApplicationDate": "06/04/2021 11:00:00",\n\t"ApplicationsData": {\n\t\t"Count": 15,\n\t\t"Since": "06/01/2021 00:00:00"\n\t},\n\t"ViewInfo": {\n\t\t"ViewDate": "05/02/2019 10:04:50",\n\t\t"ViewedBy": "OtherRecruiter",\n\t\t"UpdatedSinceViewed": true\n\t},\n\t"ApplicationsSummary": [\n\t\t{\n\t\t\t"Locations": [\n\t\t\t\t"City of London, London"\n\t\t\t],\n\t\t\t"NormalisedJobTitle": "finance project manager;project manager insurance",\n\t\t\t"Quantity": 2\n\t\t},\n\t\t{\n\t\t\t"Locations": [\n\t\t\t\t"City of London (EC1)",\n\t\t\t\t"City of London, London"\n\t\t\t],\n\t\t\t"NormalisedJobTitle": "programme manager",\n\t\t\t"Quantity": 2\n\t\t}\n\t],\n\t"CvSnippet": "Example data with <mark>mark tags</mark> for highlighting.",\n\t"LastActiveDate": "06/04/2021 11:00:00",\n\t"JobType": [\n\t\t"Permanent"\n\t],\n\t"RegisteredDate": "25/11/2018 19:15:01"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="searchresultcandidate">WebsiteLink Object</h4>
                    <p>
                        The WebsiteLink model returned in <Link to="#searchresultcandidate">Candidate</Link> object.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>URL</td>
                                <td>string</td>
                                <td>social media URL string.</td>
                            </tr>
                            <tr>
                                <td>Type</td>
                                <td>string</td>
                                <td>URL provider.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"Url": "https://www.linkedin.com/in/john-smith-12345",\n\t"Type": "linkedin"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="candidatesalaryresponse">CandidateSalary Object</h4>
                    <p>
                        The CandidateSalary model returned in <Link to="#searchresultcandidate">Candidate</Link> object.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>LowerValue</td>
                                <td>float</td>
                                <td>Lowest rate value.</td>
                            </tr>
                            <tr>
                                <td>UpperValue</td>
                                <td>float</td>
                                <td>Highest rate value.</td>
                            </tr>
                            <tr>
                                <td>RateType</td>
                                <td>string</td>
                                <td>
                                    Returns 1 of 3 values <code>Year</code>, <code>Day</code> &amp; <code>Hour</code>.
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"LowerValue": 9,\n\tUpperValue": 9.99,\n\t"RateType": "Hour"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="applicationsdata">ApplicationsData Object</h4>
                    <p>
                        The ApplicationsData model returned in <Link to="#searchresultcandidate">Candidate</Link> object.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Count</td>
                                <td>integer</td>
                                <td>
                                    Number of applications since LastActivityDate in search criteria (limited to 3 months from now).
                                </td>
                            </tr>
                            <tr>
                                <td>Since</td>
                                <td>date</td>
                                <td>Date used for calculating applications count.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"Count": 15,\n\t"Since": "06/01/2021 00:00:00"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="viewinfo">ViewInfo Object</h4>
                    <p>
                        The ViewInfo model returned in <Link to="#searchresultcandidate">Candidate</Link> object.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>ViewDate</td>
                                <td>date</td>
                                <td>
                                    Date when currently logged in recruiter or recruiter from the same company viewed candidate&apos;s
                                    profile.
                                </td>
                            </tr>
                            <tr>
                                <td>ViewedBy</td>
                                <td>string</td>
                                <td>
                                    Available values: <code>None</code>, <code>Me</code>, <code>OtherRecruiter</code> (from the same
                                    company).
                                </td>
                            </tr>
                            <tr>
                                <td>UpdatedSinceViewed</td>
                                <td>boolean</td>
                                <td>Candidate profile has been updated since ViewDate.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"ViewDate": "05/02/2019 10:04:50",\n\t"ViewedBy": "OtherRecruiter",\n\t"UpdatedSinceViewed": true\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="applicationssummary">ApplicationsSummary Object</h4>
                    <p>
                        The ApplicationsSummary model returned in <Link to="#searchresultcandidate">Candidate</Link> object.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Locations</td>
                                <td>string array</td>
                                <td>Aggregated locations.</td>
                            </tr>
                            <tr>
                                <td>NormalisedJobTitle</td>
                                <td>string</td>
                                <td>Standard job title calcualted by TotalJobs.</td>
                            </tr>
                            <tr>
                                <td>Quantity</td>
                                <td>integer</td>
                                <td>Number of applications for given NormalisedJobtitle.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"Locations": [\n\t\t"City of London (EC1)",\n\t\t"City of London, London"\n\t],\n\t"NormalisedJobTitle": "programme manager",\n\t"Quantity": 2\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="facetobjectarray">Facets Array</h4>
                    <p>
                        The facet model returned in <Link to="#searchresult">Search result</Link> object.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Type</td>
                                <td>string</td>
                                <td>Unique facet type strong name.</td>
                            </tr>
                            <tr>
                                <td>Items</td>
                                <td>object array</td>
                                <td>
                                    Returns an array of <Link to="#facetitem">facet item</Link> objects.
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {/* eslint-disable-next-line */}
                            {`"Facets": [\n\t{\n\t\t"Type": "JD",\n\t\t"Items": [\n\t\t\t{\n\t\t\t\t"Count": 938,\n\t\t\t\t"Value": "Project Coordinator",\n\t\t\t\t"IsSelected": false\n\t\t\t},\n\t\t\t\{\n\t\t\t\t"Count": 780,\n\t\t\t\t"Value": "Project Manager",\n\t\t\t\t"IsSelected": false\n\t\t\t},…\n\t\t]\n\t},…\n]`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4>Facet Item Object</h4>
                    <p>
                        The facet model returned in <Link to="#facetobjectarray">Facets array</Link>.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Count</td>
                                <td>integer</td>
                                <td>Total number of candidates that have given value selected in their profile.</td>
                            </tr>
                            <tr>
                                <td>Value</td>
                                <td>string</td>
                                <td>Value for given facet.</td>
                            </tr>
                            <tr>
                                <td>IsSelected</td>
                                <td>boolean</td>
                                <td>Flag that indicates if given facet value was used in request model.</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"Type": "Towns",\n\t"Items": [\n\t\t{\n\t\t\t"Count": 13322,\n\t\t\t"Value": "South West London",\n\t\t\t"IsSelected": false\n\t\t},\n\t\t{\n\t\t\t"Count": 10035,\n\t\t\t"Value": "South East London",\n\t\t\t"IsSelected": false\n\t\t},…\n\t]\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
        </section>
    )
}

export default SearchResultSection
